import {
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    inject,
    Signal,
    signal,
    WritableSignal,
    computed,
    effect,
    OnInit,
    DestroyRef,
    viewChild
} from '@angular/core';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { ActivatedRoute, RouterModule, Router } from '@angular/router';
import { RequestService } from '../shared/services/request.service';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    filter,
    finalize,
    forkJoin,
    map,
    switchMap,
    take,
    tap
} from 'rxjs';
import { IIroNamRequest } from '@shs-digi-care-monorepo/interfaces';
import { ReactiveFormsModule } from '@angular/forms';
import { select } from '@ngxs/store';
import {
    LoggingService,
    MultiSelectFilterComponent,
    MultiSelectFilterOption as MSFO,
    PageLayoutComponent,
    RequestStatusComponent,
    SnackbarService,
    SortOrder,
    TeamState,
    UserService,
    UserState,
    ShToggleAccessorComponent
} from '@shs-digi-care-monorepo/frontend-lib';
import { AuxiliaryRequestState, Flags, ITeam, IUser, RequestState, State } from '@shs-digi-care-monorepo/interfaces';
import { TextValueAccessor } from '@shui/angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StatusFilterValue, TabFilterState } from './state/tab-filter-state';
import { CreatedRequestsTableComponent } from './components/created-requests-table/created-requests-table.component';
import { MdlaRequestsTableComponent } from './components/mdla-requests-table/mdla-requests-table.component';
import { MyResponseNeededTableComponent } from './components/my-response-needed-table/my-response-needed-table.component';
import { MatDialog } from '@angular/material/dialog';
import { DeleteRequestDialogComponent } from './components/delete-request-dialog/delete-request-dialog.component';

enum TaskTab {
    CREATED_REQUESTS = 'created_requests',
    MDLA_REQUESTS = 'mdla_requests',
    MY_RESPONSE_NEEDED = 'my_response_needed'
}

enum RequestsMode {
    TASKS = 'tasks',
    REQUESTS = 'requests'
}

@Component({
    selector: 'app-tasks',
    templateUrl: './tasks.component.html',
    imports: [
        PageLayoutComponent,
        TranslateModule,
        RouterModule,
        ReactiveFormsModule,
        MultiSelectFilterComponent,
        TranslatePipe,
        TextValueAccessor,
        CreatedRequestsTableComponent,
        MdlaRequestsTableComponent,
        MyResponseNeededTableComponent,
        ShToggleAccessorComponent,
        RequestStatusComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class TasksComponent implements OnInit {
    private requestService = inject(RequestService);
    private userService = inject(UserService);
    private destroyRef = inject(DestroyRef);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private dialog = inject(MatDialog);
    private snackbar = inject(SnackbarService);
    private logger = inject(LoggingService);

    protected createdRequestsTable = viewChild<CreatedRequestsTableComponent>('createdRequestsTable');

    protected mode: RequestsMode = this.route.snapshot.data['mode'];
    protected me: Signal<IUser | null> = select(UserState.me);

    // Static filter options (shared across tabs)
    protected teams: Signal<ITeam[]> = select(TeamState.namTeams);
    protected teamOptions = computed<MSFO<ITeam>[]>(() =>
        this.teams().map((team) => ({ value: team, label: team.name }))
    );
    protected flagOptions: MSFO<Flags>[] = [
        Flags.IS_URGENT,
        Flags.IS_ON_HOLD,
        Flags.IS_STANDARD,
        Flags.IS_REOPENED,
        Flags.IS_QUALITY
    ].map((flag) => ({
        value: flag,
        label: 'internal-dashboard.filter.flags.' + flag
    }));

    protected mdlaRequestStatusOptions = computed<StatusFilterValue[]>(() => [
        RequestState.SUBMITTED,
        RequestState.UNDER_REVIEW,
        RequestState.PENDING_CUSTOMER_ACTION,
        RequestState.REFERRED_OUT,
        AuxiliaryRequestState.REFERRED_OUT_TO_ME,
        RequestState.CLOSED
    ]);

    protected createdRequestStatusOptions = computed<StatusFilterValue[]>(() => [
        RequestState.SAVED,
        RequestState.SUBMITTED,
        RequestState.PENDING_CUSTOMER_ACTION,
        RequestState.REFERRED_OUT,
        RequestState.UNDER_REVIEW,
        RequestState.CLOSED
    ]);

    protected statusOptionsForTab = computed<MSFO<StatusFilterValue>[]>(() => {
        const translationPrefix = this.selectedTab() === TaskTab.CREATED_REQUESTS ? 'external' : 'internal';
        return (
            this.selectedTab() === TaskTab.CREATED_REQUESTS
                ? this.createdRequestStatusOptions()
                : this.mdlaRequestStatusOptions()
        ).map((status) => ({
            value: status,
            label: `${translationPrefix}.${status}`
        }));
    });
    protected requestorOptions: MSFO<string>[] = ['all', 'internal', 'external'].map((option) => ({
        value: option,
        label: 'internal-dashboard.filter.request-source.' + option
    }));
    protected stateOptions: MSFO<State>[] = Object.values(State)
        .filter((t): t is State => typeof t !== 'number')
        .map((state) => ({
            value: state,
            label: state.toLowerCase()
        }));

    protected requestStatusBarOptions: StatusFilterValue[] = [
        RequestState.SUBMITTED,
        RequestState.UNDER_REVIEW,
        RequestState.PENDING_CUSTOMER_ACTION,
        RequestState.REFERRED_OUT,
        AuxiliaryRequestState.REFERRED_OUT_TO_ME,
        RequestState.CLOSED
    ];

    // Per-tab filter state — each tab maintains its own independent filters, pagination & sorting
    protected readonly tabStates: Record<TaskTab, TabFilterState> = {
        [TaskTab.CREATED_REQUESTS]: new TabFilterState([], ['updated_at'], SortOrder.DESC),
        [TaskTab.MDLA_REQUESTS]: new TabFilterState([RequestState.SUBMITTED], ['submitted_date'], SortOrder.DESC),
        [TaskTab.MY_RESPONSE_NEEDED]: new TabFilterState([], ['updated_at'], SortOrder.DESC)
    };

    // Active tab
    protected selectedTab: WritableSignal<TaskTab> = signal(this.initSelectedTab());
    protected TaskTab = TaskTab;
    protected RequestsMode = RequestsMode;

    /** Convenience accessor for the currently active tab's filter state */
    protected activeState = computed<TabFilterState>(() => this.tabStates[this.selectedTab()]);

    protected showMyAssignedTasksToggle = computed(() => {
        const statuses = this.activeState().statusFilterValue();
        return !(statuses.length === 1 && statuses[0] === RequestState.SUBMITTED);
    });

    // Table data (per tab)
    protected tasks: WritableSignal<IIroNamRequest[]> = signal([]);
    protected total: WritableSignal<number> = signal(0);
    protected loading: WritableSignal<boolean> = signal(false);

    // Delete saved requests
    protected readonly deletingRequests = signal(false);

    constructor() {
        effect(() => {
            // Re-read all reactive signals from the active tab to trigger on any filter change
            const state = this.activeState();
            // Read all signals so the effect tracks them
            state.page();
            state.pageSize();
            state.statusFilterValue();
            state.flagsFilterValue();
            state.teamFilterValue();
            state.requestorFilterValue();
            state.requestorEmailFilterValue();
            state.stateFilterValue();
            state.assigneeFilterValue();
            state.searchTermValue();
            state.orderBy();
            state.sortOrder();
            state.onlyMyAssignedTasks();
            state.globalSearchActive();

            this.loadRequests();
            if (this.selectedTab() === TaskTab.MDLA_REQUESTS) {
                this.loadFilterOptions();
            }
            this.saveFilterState();
        });
        effect(() => {
            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: { tab: this.selectedTab() },
                queryParamsHandling: 'merge'
            });
        });
    }

    public ngOnInit(): void {
        this.logger.info('Tasks page initialized', { mode: this.mode });
        this.loadUserSettings();
        this.restoreFilterStateFromLocalStorage();
        // Wire up search debounce for each tab independently
        for (const state of Object.values(this.tabStates)) {
            state.searchControl.valueChanges
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    distinctUntilChanged(),
                    debounceTime(300),
                    tap((value) => state.searchTermValue.set(value ? value : undefined))
                )
                .subscribe();
        }
    }

    protected changeSorting(column: string, sortOrder: SortOrder | null): void {
        const state = this.activeState();
        if (sortOrder === null) {
            state.orderBy.set(['submitted_date']);
            state.sortOrder.set(SortOrder.DESC);
            return;
        }

        state.orderBy.set([column]);
        state.sortOrder.set(sortOrder);
    }

    protected assignToMe(_event: { request: IIroNamRequest; user: IUser }): void {
        // IRO requests don't have a local assignee field; reload the table instead
        this.loadRequests();
    }

    protected deleteSelectedRequests(selected: IIroNamRequest[]): void {
        if (selected.length === 0) {
            return;
        }

        this.logger.info('Requesting deletion of selected IRO requests', { count: selected.length });
        this.dialog
            .open(DeleteRequestDialogComponent, {
                width: '500px',
                data: { count: selected.length }
            })
            .afterClosed()
            .pipe(
                filter((result) => !!result),
                take(1),
                switchMap(() => {
                    this.logger.info('Deleting IRO requests', { count: selected.length });
                    this.deletingRequests.set(true);
                    return forkJoin(selected.map((r) => this.requestService.deleteRequest(r.id)));
                }),
                tap(() => {
                    this.logger.info('IRO requests deleted successfully', { count: selected.length });
                    this.snackbar.success('common.form.delete-success');
                    this.createdRequestsTable()?.clearSelection();
                    this.loadRequests();
                }),
                catchError((err) => {
                    this.logger.error('Failed to delete IRO requests', { error: err?.message ?? err });
                    this.snackbar.error('common.form.delete-error');
                    throw err;
                }),
                finalize(() => {
                    this.deletingRequests.set(false);
                })
            )
            .subscribe();
    }

    protected onGlobalSearchToggle(active: boolean): void {
        const state = this.activeState();
        if (active) {
            state.savedFilterSnapshot = {
                statusFilterValue: state.statusFilterValue(),
                flagsFilterValue: state.flagsFilterValue(),
                teamFilterValue: state.teamFilterValue(),
                requestorFilterValue: state.requestorFilterValue(),
                requestorEmailFilterValue: state.requestorEmailFilterValue(),
                stateFilterValue: state.stateFilterValue(),
                assigneeFilterValue: state.assigneeFilterValue(),
                onlyMyAssignedTasks: state.onlyMyAssignedTasks()
            };
            state.statusFilterValue.set([]);
            state.flagsFilterValue.set([]);
            state.teamFilterValue.set([]);
            state.requestorFilterValue.set([]);
            state.requestorEmailFilterValue.set([]);
            state.stateFilterValue.set([]);
            state.assigneeFilterValue.set([]);
            state.onlyMyAssignedTasks.set(false);
        } else if (state.savedFilterSnapshot) {
            state.statusFilterValue.set(state.savedFilterSnapshot.statusFilterValue);
            state.flagsFilterValue.set(state.savedFilterSnapshot.flagsFilterValue);
            state.teamFilterValue.set(state.savedFilterSnapshot.teamFilterValue);
            state.requestorFilterValue.set(state.savedFilterSnapshot.requestorFilterValue);
            state.requestorEmailFilterValue.set(state.savedFilterSnapshot.requestorEmailFilterValue);
            state.stateFilterValue.set(state.savedFilterSnapshot.stateFilterValue);
            state.assigneeFilterValue.set(state.savedFilterSnapshot.assigneeFilterValue);
            state.onlyMyAssignedTasks.set(state.savedFilterSnapshot.onlyMyAssignedTasks);
            state.savedFilterSnapshot = null;
        }
        state.globalSearchActive.set(active);
    }

    protected onStatusBarChange(statuses: StatusFilterValue[]): void {
        if (statuses.length === 0) {
            this.snackbar.warning('common.tasks.at-least-one-status');
            return;
        }
        this.activeState().statusFilterValue.set(statuses);
    }

    protected onStatusFilterChange(statuses: StatusFilterValue[]): void {
        if (statuses.length === 0) {
            this.snackbar.warning('common.tasks.at-least-one-status');
            return;
        }
        this.activeState().statusFilterValue.set(statuses);
    }

    private resolveAssigneeFilter(state: TabFilterState): string[] | undefined {
        const filterAssignees = state.assigneeFilterValue();
        const myGid = state.onlyMyAssignedTasks() && this.me()?.gid ? [this.me()?.gid ?? ''] : [];
        const merged = [...new Set([...filterAssignees, ...myGid])];
        return merged.length > 0 ? merged : undefined;
    }

    private loadRequests(): void {
        const state = this.activeState();
        const commonFilters = {
            page: state.page() - 1,
            pageSize: state.pageSize(),
            orderBy: state.orderBy(),
            sortOrder: state.sortOrder(),
            searchTerm: state.searchTermValue()
        };

        const isGlobal = state.globalSearchActive();

        const referredOutToMe = isGlobal
            ? false
            : state.statusFilterValue().includes(AuxiliaryRequestState.REFERRED_OUT_TO_ME);
        const statusValues = isGlobal
            ? []
            : (state
                  .statusFilterValue()
                  .filter((s) => s !== AuxiliaryRequestState.REFERRED_OUT_TO_ME) as RequestState[]);
        // When "referred out to me" is selected, ensure REFERRED_OUT is included as the actual status sent to the API
        if (referredOutToMe && !statusValues.includes(RequestState.REFERRED_OUT)) {
            statusValues.push(RequestState.REFERRED_OUT);
        }

        const filters = {
            ...commonFilters,
            status: statusValues,
            referredOutToMe: referredOutToMe ? true : undefined,
            requestorEmails: isGlobal ? undefined : state.requestorEmailFilterValue(),
            team: isGlobal ? undefined : state.teamFilterValue().map((t) => t.id),
            flags: isGlobal ? undefined : state.flagsFilterValue().length > 0 ? state.flagsFilterValue() : undefined,
            assignees: isGlobal ? undefined : this.resolveAssigneeFilter(state),
            state: isGlobal ? undefined : state.stateFilterValue().length > 0 ? state.stateFilterValue() : undefined
        };

        const tab = this.selectedTab();
        const request$ = {
            [TaskTab.MDLA_REQUESTS]: () => this.requestService.getAssignedToMyTeams(filters),
            [TaskTab.CREATED_REQUESTS]: () => this.requestService.getMyRequests(filters),
            [TaskTab.MY_RESPONSE_NEEDED]: () => this.requestService.getMyResponseNeededRequests(commonFilters)
        }[tab]();

        this.loading.set(true);

        request$
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                map((paginatedRequests) => {
                    this.total.set(paginatedRequests.metadata.itemCount);
                    return paginatedRequests.items;
                }),
                tap((tasks) => {
                    this.logger.debug('Loaded IRO requests', { tab, count: tasks.length, total: this.total() });
                    this.tasks.set(tasks);
                    this.loading.set(false);
                }),
                catchError((error) => {
                    this.logger.error('Failed to load IRO requests', { tab, error: error?.message ?? error });
                    this.tasks.set([]);
                    this.loading.set(false);
                    return [];
                })
            )
            .subscribe();
    }

    private loadFilterOptions(): void {
        const state = this.activeState();
        const statusForFilters = state.globalSearchActive()
            ? [RequestState.SUBMITTED]
            : (state
                  .statusFilterValue()
                  .filter((s) => s !== AuxiliaryRequestState.REFERRED_OUT_TO_ME) as RequestState[]);
        this.requestService
            .getAssignedToMyTeamsFilterOptions(statusForFilters)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap((options) => {
                    state.requestorEmailOptions.set(
                        options.requestors.map((requestor) => ({ label: requestor, value: requestor }))
                    );
                }),
                switchMap((options) =>
                    this.userService.getUsernamesByGIDs(options.assignees).pipe(
                        tap((users) => {
                            state.assigneeOptions.set(
                                users.map(({ gid, firstName, lastName }) => ({
                                    label: `${firstName} ${lastName}`,
                                    value: gid
                                }))
                            );
                        }),
                        catchError(() => {
                            state.assigneeOptions.set(options.assignees.map((gid) => ({ label: gid, value: gid })));
                            return [];
                        })
                    )
                ),
                catchError(() => {
                    state.requestorEmailOptions.set([]);
                    state.assigneeOptions.set([]);
                    return [];
                })
            )
            .subscribe();
    }

    private loadUserSettings(): void {
        this.userService
            .getUserSettings()
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap((settings) => {
                    const pageSize = settings.defaultPageSize ?? 20;
                    for (const state of Object.values(this.tabStates)) {
                        state.pageSize.set(pageSize);
                    }
                }),
                catchError(() => [])
            )
            .subscribe();
    }

    private saveFilterState(): void {
        const state = this.activeState();
        const filterState = {
            page: state.page(),
            pageSize: state.pageSize(),
            status: state.statusFilterValue(),
            flags: state.flagsFilterValue(),
            team: state.teamFilterValue().map((t) => (typeof t === 'object' ? t.id : t)),
            requestor: state.requestorFilterValue(),
            requestorEmail: state.requestorEmailFilterValue(),
            state: state.stateFilterValue(),
            assignee: state.assigneeFilterValue(),
            onlyMyAssignedTasks: state.onlyMyAssignedTasks(),
            sortOrder: state.sortOrder(),
            orderBy: state.orderBy(),
            searchTerm: state.searchTermValue() ?? ''
        };
        sessionStorage.setItem('tasksFilterState', JSON.stringify(filterState));
        sessionStorage.setItem('activeStatuses', JSON.stringify(state.statusFilterValue()));
        sessionStorage.setItem('activeTab', this.selectedTab());
    }

    private static readonly VALID_ORDER_BY = new Set([
        'created_at',
        'updated_at',
        'submitted_date',
        'subject',
        'classification',
        'assignees',
        'state',
        'email',
        'referred_out_team',
        'referred_out_email'
    ]);

    private restoreFilterStateFromLocalStorage(): void {
        const saved = sessionStorage.getItem('tasksFilterState');
        if (!saved) return;
        const filterState = JSON.parse(saved);
        const state = this.activeState();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const mappings: [keyof typeof filterState, (value: any) => void][] = [
            ['page', (v) => state.page.set(v)],
            ['pageSize', (v) => state.pageSize.set(v)],
            ['status', (v) => state.statusFilterValue.set(v)],
            ['flags', (v) => state.flagsFilterValue.set(v)],
            ['team', (v) => state.teamFilterValue.set(v)],
            ['requestor', (v) => state.requestorFilterValue.set(v)],
            ['requestorEmail', (v) => state.requestorEmailFilterValue.set(v)],
            ['state', (v) => state.stateFilterValue.set(v)],
            ['assignee', (v) => state.assigneeFilterValue.set(v)],
            ['onlyMyAssignedTasks', (v) => state.onlyMyAssignedTasks.set(v)],
            ['sortOrder', (v) => state.sortOrder.set(v)],
            [
                'orderBy',
                (v) => {
                    const valid = Array.isArray(v)
                        ? v.filter((col: string) => TasksComponent.VALID_ORDER_BY.has(col))
                        : [];
                    if (valid.length > 0) {
                        state.orderBy.set(valid);
                    }
                }
            ],
            ['searchTerm', (v) => state.searchTermValue.set(v === '' ? undefined : v)]
        ];
        mappings.forEach(([key, setter]) => {
            if (filterState[key] !== undefined) {
                setter(filterState[key]);
            }
        });
    }

    private initSelectedTab(): TaskTab {
        const tabParam = this.route.snapshot.queryParamMap.get('tab');
        if (tabParam && Object.values(TaskTab).includes(tabParam as TaskTab)) {
            return tabParam as TaskTab;
        }
        const savedTab = sessionStorage.getItem('activeTab');
        if (savedTab && Object.values(TaskTab).includes(savedTab as TaskTab)) {
            return savedTab as TaskTab;
        }
        return this.mode === RequestsMode.TASKS ? TaskTab.MDLA_REQUESTS : TaskTab.MY_RESPONSE_NEEDED;
    }
}
